import { parseDbTimestamp } from "@repo/shared";
import type {
  ComplianceClientRow,
  EffectiveScreeningStatus,
  LastUpdatedFilter,
  MatchDisposition,
  ScreeningDecision,
  ScreeningFilter,
  Translated,
  VerifiedIdFilter,
} from "@repo/shared/types";
import { matchesLastUpdatedFilter, matchesVerifiedIdFilter, matchKey } from "@repo/shared/types";
import { Building2, User, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SortableTableHead,
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowLink,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import {
  useClientIdentity,
  VerifiedIdBadge,
  VerifiedIdDialog,
  VerifiedIdSubjects,
} from "@/components/verified-id";
import { useTableSort } from "@/hooks/use-table-sort";
import { useMe } from "@/lib/hooks/use-clients";
import {
  useClientScreenings,
  useClientsForReview,
  useComplianceViews,
  useRecordReview,
} from "@/lib/hooks/use-compliance";
import { usePagination } from "@/lib/hooks/use-pagination";
import { useT } from "@/lib/i18n/use-t";
import { hasPermission, type Permission } from "@/lib/permissions";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./page.i18n";
import { ReviewDialog } from "./review-dialog";

type Labels = Translated<typeof T>;

/** Yes / No for a flag, or a dash when there is nothing to report. */
function flagLabel(c: ComplianceClientRow, flag: "pep" | "sanctioned", t: Labels): string {
  if (c.findings === null) return "—";
  const on = flag === "pep" ? c.findings.isPep : c.findings.isSanctioned;
  // Tri-state: null is "nobody was asked", which is neither Yes nor No. The
  // old version keyed this off `type === "Company"`, which silently missed
  // Group and, worse, hid the gap in the UI while the stored status still
  // said "clear".
  if (on === null || on === undefined) return t.notAssessed;
  return on ? t.yes : t.no;
}

/** Who the PEP finding is about — the client, or their representative. */
function subjectLabel(c: ComplianceClientRow, t: Labels): string {
  if (!c.subject) return "—";
  if (c.subject.kind === "self") return t.subjectSelf;
  if (c.subject.kind === "none") return t.subjectNone;
  return c.subject.name ?? t.subjectSelf;
}

function statusLabel(c: ComplianceClientRow, t: Labels): string {
  return (
    {
      not_checked: t.never,
      failed: t.failedShort,
      clear: t.filterClear,
      incomplete: t.incomplete,
      needs_review: t.needsReview,
      accepted: t.accepted,
      blocked: t.blocked,
      not_a_match: t.notAMatch,
    }[c.status] ?? c.status
  );
}

const DEFAULT_VIEW_ID = "standard-all-clients";

const SCREENING_FILTERS = new Set<ScreeningFilter>([
  "all",
  "needs_review",
  "incomplete",
  "pep",
  "sanctioned",
  "accepted",
  "blocked",
  "clear",
  "failed",
  "not_checked",
]);

const VERIFIED_ID_FILTERS = new Set<VerifiedIdFilter>([
  "all",
  "verified",
  "verified_1y",
  "verified_3y",
  "not_verified",
]);

/**
 * Status → badge variant. `needs_review` is amber (attention, not an error),
 * `blocked` red, `accepted` and `clear` green — an accepted hit is a resolved
 * state, which is the whole point of reviewing it.
 */
const STATUS_VARIANT: Record<
  EffectiveScreeningStatus,
  "secondary" | "success" | "warning" | "destructive"
> = {
  not_checked: "secondary",
  failed: "secondary",
  clear: "success",
  // Amber, like needs_review: something is outstanding. Deliberately NOT green
  // — the sanction half passed but the PEP half was never asked, and a green
  // badge there is the false "clear" this whole state exists to replace.
  incomplete: "warning",
  // Green like `clear`: a compliance user established the listing is somebody
  // else, so nothing is outstanding. The distinct LABEL is what preserves the
  // fact that a human ruled it out rather than the lists coming back empty.
  not_a_match: "success",
  needs_review: "warning",
  accepted: "success",
  blocked: "destructive",
};

/**
 * Whether a client matches the chosen filter. `pep` and `sanctioned` are
 * findings and match independently — a client who is both matches either. The
 * rest are the resolved status, so "needs_review" means a hit nobody has signed
 * off and "accepted" means one somebody has.
 */
function matchesFilter(c: ComplianceClientRow, filter: ScreeningFilter) {
  if (filter === "all") return true;
  if (filter === "pep") return c.findings?.isPep === true;
  if (filter === "sanctioned") return c.findings?.isSanctioned === true;
  return c.status === filter;
}

/** One sortable row per client: the client, plus the values the cells render. */
type Row = {
  client: ComplianceClientRow;
  name: string;
  /** Sort keys for the two flag columns; "—" keeps unscreened rows together. */
  pep: string;
  sanctioned: string;
  /** Who the PEP finding is about — the client, or a named representative. */
  subject: string;
  status: string;
  lastChecked: string;
  /** Epoch ms behind the rendered date (0 = never), so the column sorts chronologically. */
  lastCheckedTs: number;
};

/**
 * One PEP / Sanctioned cell. Variants deliberately mirror the flow's own
 * PEP/Sanction lookup widget (`trapets-lookup-result.tsx`) so the same finding
 * reads the same in both places: amber for a PEP hit, red for a sanction hit,
 * green for a clean flag. An unscreened or failed check gets no badge at all —
 * "no hit" and "never asked" must not look alike.
 *
 * `on === null` is the third case, and the important one: the PEP question was
 * never put to a natural person, because the client is a Company or Group with
 * no registered representative. It renders amber, not green and not a neutral
 * dash — the dash the first cut used was keyed off `type === "Company"`, which
 * missed Group entirely and, worse, only hid the gap in the UI while the stored
 * status still resolved to "clear" and passed the compliance gate.
 */
function FlagCell({
  findings,
  on,
  tone = "warning",
  children,
}: {
  findings: ComplianceClientRow["findings"];
  on: boolean | null | undefined;
  tone?: "warning" | "destructive";
  children: React.ReactNode;
}) {
  if (findings === null) return <span className="text-muted-foreground">{children}</span>;
  if (on === null || on === undefined) return <Badge variant="warning">{children}</Badge>;
  return <Badge variant={on ? tone : "success"}>{children}</Badge>;
}

export default function ComplianceClientsReviewPage() {
  const navigate = useNavigate();
  const { formatDate } = useRegionalFormat();
  const { me, isLoading: meLoading } = useMe();
  const t = useT(T);
  const userPermissions = (me?.permissions || ["advisor"]) as Permission[];
  const allowed = hasPermission(userPermissions, "compliance");

  const [viewId, setViewId] = useState<string>(DEFAULT_VIEW_ID);
  // QL-21 — `?screening=` seeds the filter so the batch results step can hand
  // the reviewer straight to the rows that still need a verdict. Read once as
  // the initial value; changing the dropdown afterwards is plain local state.
  const [searchParams, setSearchParams] = useSearchParams();
  const [screeningFilter, setScreeningFilter] = useState<ScreeningFilter>(() => {
    const raw = searchParams.get("screening");
    return raw && SCREENING_FILTERS.has(raw as ScreeningFilter) ? (raw as ScreeningFilter) : "all";
  });
  // QL-357 — `?verifiedId=` seeds this one the same way, so the dashboard's
  // Verified ID widget can open the list on the clients without one.
  const [verifiedIdFilter, setVerifiedIdFilter] = useState<VerifiedIdFilter>(() => {
    const raw = searchParams.get("verifiedId");
    return raw && VERIFIED_ID_FILTERS.has(raw as VerifiedIdFilter)
      ? (raw as VerifiedIdFilter)
      : "all";
  });
  const [lastUpdatedFilter, setLastUpdatedFilter] = useState<LastUpdatedFilter>("anytime");
  const [search, setSearch] = useState("");
  const [reviewing, setReviewing] = useState<ComplianceClientRow | null>(null);
  // QL-e — which row's Verified ID modal is open. Held here rather than per row
  // so only one dialog mounts at a time in a table of hundreds.
  const [identityFor, setIdentityFor] = useState<ComplianceClientRow | null>(null);
  const { identity: identityDetail } = useClientIdentity(identityFor?.clientId ?? null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const recordReview = useRecordReview();
  // QL-b — the matched individuals to adjudicate. The list row carries only the
  // cached findings, so the match detail comes from the screening log, fetched
  // only while a client is actually open for review.
  const { data: screenings } = useClientScreenings(reviewing?.clientId ?? null);
  const reviewIndividuals = useMemo(() => {
    const newest = (screenings ?? []).find((row) => row.outcome === "checked");
    if (!newest) return [];
    const all = [
      ...(newest.result?.matchedIndividuals ?? []),
      ...(newest.representativeResult?.matchedIndividuals ?? []),
    ];
    // Both halves of a Company screening can return the same person; the server
    // keys dispositions by matchKey, so show each person once.
    return [...new Map(all.map((i) => [matchKey(i), i])).values()];
  }, [screenings]);

  useEffect(() => {
    if (!meLoading && !allowed) navigate("/", { replace: true });
  }, [meLoading, allowed, navigate]);

  const { data: views = [] } = useComplianceViews();
  const { data: clients, isLoading } = useClientsForReview(viewId, !meLoading && allowed);

  // The view filter is server-side (it reuses the stored view definitions); the
  // screening, Verified ID, last-updated and name filters are cheap matches
  // over rows already in hand, like the approvals page does with its tabs.
  //
  // Memoised on the inputs rather than rebuilt per render: `useTableSort` and
  // `usePagination` both key their own memos on the array identity, so a fresh
  // array every render would re-sort and re-slice the whole list whenever an
  // unrelated dialog opens or closes.
  //
  // `now` is pinned once per pass so every row is measured against the same
  // instant — a per-row `new Date()` can land either side of midnight mid-list
  // and drop a row out of "Today" while its neighbour stays.
  const query = search.trim().toLowerCase();
  const rows: Row[] = useMemo(() => {
    const now = new Date();
    return (clients ?? [])
      .filter((c) => matchesFilter(c, screeningFilter))
      .filter((c) => matchesVerifiedIdFilter(c.identity, verifiedIdFilter, now))
      .filter((c) => matchesLastUpdatedFilter(c.updatedAt, lastUpdatedFilter, now))
      .filter((c) => query === "" || c.name.toLowerCase().includes(query))
      .map((c) => {
        // Same bare-Postgres-timestamp reading as the filters above.
        const lastCheckedAt = parseDbTimestamp(c.lastCheckedAt);
        return {
          client: c,
          name: c.name,
          pep: flagLabel(c, "pep", t),
          sanctioned: flagLabel(c, "sanctioned", t),
          subject: subjectLabel(c, t),
          status: statusLabel(c, t),
          lastChecked: lastCheckedAt ? formatDate(lastCheckedAt) : t.never,
          lastCheckedTs: lastCheckedAt?.getTime() ?? 0,
        };
      });
  }, [clients, screeningFilter, verifiedIdFilter, lastUpdatedFilter, query, t, formatDate]);

  const { sorted, directionFor, toggleSort } = useTableSort(rows, "name");
  const { page, setPage, pageSize, totalCount, totalPages, pageItems } = usePagination(sorted, 25);

  // Narrowing the list while parked on a later page otherwise leaves the
  // reviewer looking at an empty table: `usePagination` clamps to the last
  // page that still exists, which is not necessarily one with the new hits on.
  useEffect(() => {
    setPage(1);
  }, [viewId, screeningFilter, verifiedIdFilter, lastUpdatedFilter, query, setPage]);

  const submitReview = async (
    decision: ScreeningDecision,
    note: string,
    matchDispositions: Record<string, MatchDisposition>,
  ) => {
    setReviewError(null);
    if (!reviewing) return;
    try {
      await recordReview.mutateAsync({
        clientId: reviewing.clientId,
        decision,
        note,
        // Omit entirely when nothing was adjudicated, so a review on a client
        // with no matched individuals stays exactly as it was before QL-b.
        ...(Object.keys(matchDispositions).length > 0 && { matchDispositions }),
      });
      setReviewing(null);
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : t.reviewFailed);
    }
  };

  if (meLoading || !allowed) return null;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.compliance, href: "/compliance" }, { label: t.title }]}
        title={t.title}
        description={t.description}
      />

      <div className="flex flex-wrap gap-4">
        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">{t.view}</span>
          <Select value={viewId} onValueChange={setViewId}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {views.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">{t.screening}</span>
          <Select
            value={screeningFilter}
            onValueChange={(v) => {
              setScreeningFilter(v as ScreeningFilter);
              // Drop the seed from the URL so a reload does not silently
              // re-apply a filter the user has since changed.
              if (searchParams.has("screening")) {
                searchParams.delete("screening");
                setSearchParams(searchParams, { replace: true });
              }
            }}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.all}</SelectItem>
              <SelectItem value="needs_review">{t.filterNeedsReview}</SelectItem>
              <SelectItem value="pep">{t.filterPep}</SelectItem>
              <SelectItem value="sanctioned">{t.filterSanctioned}</SelectItem>
              <SelectItem value="incomplete">{t.filterIncomplete}</SelectItem>
              <SelectItem value="accepted">{t.filterAccepted}</SelectItem>
              <SelectItem value="blocked">{t.filterBlocked}</SelectItem>
              <SelectItem value="clear">{t.filterClear}</SelectItem>
              <SelectItem value="failed">{t.filterFailed}</SelectItem>
              <SelectItem value="not_checked">{t.filterNotChecked}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">{t.verifiedIdFilter}</span>
          <Select
            value={verifiedIdFilter}
            onValueChange={(v) => {
              setVerifiedIdFilter(v as VerifiedIdFilter);
              if (searchParams.has("verifiedId")) {
                searchParams.delete("verifiedId");
                setSearchParams(searchParams, { replace: true });
              }
            }}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.verifiedIdAll}</SelectItem>
              <SelectItem value="verified">{t.verifiedIdVerified}</SelectItem>
              <SelectItem value="verified_1y">{t.verifiedId1y}</SelectItem>
              <SelectItem value="verified_3y">{t.verifiedId3y}</SelectItem>
              <SelectItem value="not_verified">{t.verifiedIdNot}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">{t.lastUpdated}</span>
          <Select
            value={lastUpdatedFilter}
            onValueChange={(v) => setLastUpdatedFilter(v as LastUpdatedFilter)}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="anytime">{t.lastUpdatedAnytime}</SelectItem>
              <SelectItem value="today">{t.lastUpdatedToday}</SelectItem>
              <SelectItem value="this_week">{t.lastUpdatedWeek}</SelectItem>
              <SelectItem value="this_month">{t.lastUpdatedMonth}</SelectItem>
              <SelectItem value="this_quarter">{t.lastUpdatedQuarter}</SelectItem>
              <SelectItem value="this_year">{t.lastUpdatedYear}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <span className="text-sm text-muted-foreground">{t.name}</span>
          <SearchInput
            className="w-64"
            placeholder={t.searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : sorted.length === 0 ? (
        <EmptyState>{t.empty}</EmptyState>
      ) : (
        <Table variant="navigation">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <SortableTableHead direction={directionFor("name")} onSort={() => toggleSort("name")}>
                {t.name}
              </SortableTableHead>
              <SortableTableHead direction={directionFor("pep")} onSort={() => toggleSort("pep")}>
                {t.pep}
              </SortableTableHead>
              <SortableTableHead
                direction={directionFor("sanctioned")}
                onSort={() => toggleSort("sanctioned")}
              >
                {t.sanctioned}
              </SortableTableHead>
              <SortableTableHead
                direction={directionFor("subject")}
                onSort={() => toggleSort("subject")}
                title={t.subjectRepHint}
              >
                {t.subjectColumn}
              </SortableTableHead>
              <SortableTableHead
                direction={directionFor("status")}
                onSort={() => toggleSort("status")}
              >
                {t.statusColumn}
              </SortableTableHead>
              <SortableTableHead
                direction={directionFor("lastCheckedTs")}
                onSort={() => toggleSort("lastCheckedTs")}
              >
                {t.lastChecked}
              </SortableTableHead>
              <TableActionsHead>{t.actions}</TableActionsHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageItems.map((row) => (
              <TableRow key={row.client.clientId}>
                <TableCell className="w-10">
                  {row.client.type === "Company" ? (
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  ) : row.client.type === "Group" ? (
                    <Users className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <User className="h-4 w-4 text-muted-foreground" />
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {/* The badge button sits beside (not inside) the row link, above its overlay. */}
                  <span className="inline-flex items-center gap-1.5 [&>button]:relative [&>button]:z-10">
                    <TableRowLink to={`/clients/${row.client.clientId}`}>{row.name}</TableRowLink>
                    {/* The CLIENT's badge. Only shown when the verification is
                        about the client themself — a representative's
                        verification belongs on the "PEP screened" cell below,
                        and putting it here would attribute one person's
                        verified identity to another. */}
                    {row.client.identity?.subjectKind === "self" && (
                      <VerifiedIdBadge
                        summary={row.client.identity}
                        onOpen={() => setIdentityFor(row.client)}
                      />
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  <FlagCell findings={row.client.findings} on={row.client.findings?.isPep}>
                    {row.pep}
                  </FlagCell>
                </TableCell>
                <TableCell>
                  <FlagCell
                    findings={row.client.findings}
                    on={row.client.findings?.isSanctioned}
                    tone="destructive"
                  >
                    {row.sanctioned}
                  </FlagCell>
                </TableCell>
                <TableCell
                  className={
                    row.client.subject?.kind === "none"
                      ? "text-warning text-sm"
                      : "text-muted-foreground text-sm"
                  }
                >
                  <span className="inline-flex items-center gap-1.5 [&>button]:relative [&>button]:z-10">
                    {row.subject}
                    {/* This cell names the REPRESENTATIVE for a Company/Group,
                        so the badge here is theirs, not the client's. */}
                    {row.client.identity?.subjectKind === "representative" && (
                      <VerifiedIdBadge
                        summary={row.client.identity}
                        onOpen={() => setIdentityFor(row.client)}
                      />
                    )}
                  </span>
                  {/* QL-654 - several representatives: who has verified, who is missing. */}
                  <VerifiedIdSubjects summary={row.client.identity} />
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANT[row.client.status]}>{row.status}</Badge>
                  {row.client.lastAttemptFailed && row.client.findings && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {t.lastAttemptFailed}
                    </span>
                  )}
                </TableCell>
                <TableCell>{row.lastChecked}</TableCell>
                <TableActionsCell>
                  {/* A hit OR an unassessed PEP question can be reviewed.
                      Accepting the latter is how a compliance user records that
                      the representative was screened outside the system; a
                      genuinely clean or unscreened client has nothing to sign
                      off, and the service refuses such a verdict anyway. */}
                  {row.client.findings &&
                    (row.client.findings.isPep === true ||
                      row.client.findings.isPep === null ||
                      row.client.findings.isSanctioned) && (
                      <Button variant="outline" size="sm" onClick={() => setReviewing(row.client)}>
                        {t.review}
                      </Button>
                    )}
                </TableActionsCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <VerifiedIdDialog
        open={identityFor !== null}
        onClose={() => setIdentityFor(null)}
        verifications={identityDetail?.verifications ?? []}
      />

      <ReviewDialog
        client={reviewing}
        individuals={reviewIndividuals}
        onClose={() => {
          setReviewing(null);
          setReviewError(null);
        }}
        onSubmit={submitReview}
        isPending={recordReview.isPending}
        error={reviewError}
      />
    </div>
  );
}
